import React from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';
import { Button } from '@/modules/Button';
import { faArrowTurnDownRight, faFilePdf, faDownload } from '@fortawesome/pro-regular-svg-icons';
import { faLink } from '@fortawesome/pro-solid-svg-icons';
import type { NotificationCardProps } from '@/modules/Notification/types/notification';

/**
 * Props del cuerpo enriquecido de {@link Notification}: renderiza en la columna derecha del `Alert` (vía `headerAddon`).
 */
export interface NotificationContentBodyProps {
  /** Bloque `content` de {@link Notification}. */
  content: NotificationCardProps['content'];
  /** Callbacks de acciones del bloque CTA y enlaces compartidos con la tarjeta. */
  events?: NotificationCardProps['events'];
}

const bodyRoot = 'ui-w-full ui-min-w-0 ui-max-w-full';

const PANEL_BASE =
  'ui-flex ui-min-w-0 ui-rounded-control-sm ui-bg-surface-brand-default ui-px-12 ui-py-8';

/**
 * Cuerpo enriquecido del `Notification` según `content.kind` ([Figma Content](https://www.figma.com/design/aWDHIXKjf82m6udooBUeZG/Notification?node-id=151-1635&m=dev), [sección ejemplo 242:6622](https://www.figma.com/design/aWDHIXKjf82m6udooBUeZG/Notification?node-id=242-6622&m=dev)).
 * Con `kind: 'default'` no renderiza nada (el texto va por `description` del `Alert`).
 *
 * @example Panel de mensaje citado
 * ```tsx
 * <NotificationContentBody
 *   content={{
 *     title: 'Título',
 *     kind: 'message',
 *     messageText: 'Texto citado que aparece en el panel.',
 *   }}
 * />
 * ```
 */
export const NotificationContentBody = React.memo(
  ({ content, events }: NotificationContentBodyProps) => {
    const kind = content.kind ?? 'default';
    if (kind === 'default') {
      return null;
    }

    const fileBlock = { ...content.file };
    const linkBlock = { ...content.link };
    const ctaBlock = { ...content.cta };

    if (process.env.NODE_ENV !== 'production') {
      if (kind === 'file' && !fileBlock.fileName) {
        // eslint-disable-next-line no-console
        console.warn('Notification: con `content.kind: "file"` define `content.file.fileName`.');
      }
      if (kind === 'link' && !linkBlock.href) {
        // eslint-disable-next-line no-console
        console.warn('Notification: con `content.kind: "link"` define `content.link.href`.');
      }
    }

    if (kind === 'message') {
      const text = content.messageText ?? content.description ?? '';
      if (!text) return null;
      return (
        <div className={bodyRoot}>
          <div className={cn(PANEL_BASE, 'ui-w-full ui-flex-col ui-gap-2')}>
            <div className="ui-flex ui-w-full ui-min-w-0 ui-items-start ui-gap-4">
              <div className="ui-flex ui-w-[10px] ui-shrink-0 ui-items-start ui-justify-center ui-py-4">
                <Icon name={faArrowTurnDownRight} color="tertiary" size="xs" />
              </div>
              <p
                className={cn(
                  'ui-min-w-0 ui-max-w-full ui-flex-1 ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap ui-text-body-xs-medium ui-font-body-xs-medium',
                  'ui-leading-body-xs-medium ui-tracking-body-xs ui-text-tertiary'
                )}
                title={text}
              >
                {text}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (kind === 'link') {
      const label = linkBlock.label ?? content.description ?? linkBlock.href ?? '';
      const href = linkBlock.href ?? '#';
      if (!linkBlock.href && process.env.NODE_ENV !== 'production') {
        return null;
      }
      return (
        <div className={bodyRoot}>
          <div className={cn(PANEL_BASE, 'ui-w-full ui-items-center ui-gap-8')}>
            <div className="ui-flex ui-shrink-0 ui-items-start ui-py-4">
              <Icon name={faLink} color="brand" size="xs" />
            </div>
            <a
              href={href}
              target={linkBlock.external ? '_blank' : undefined}
              rel={linkBlock.external ? 'noopener noreferrer' : undefined}
              title={label}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'ui-min-w-0 ui-max-w-full ui-flex-1 ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap ui-text-body-xs-medium ui-font-body-xs-medium',
                'ui-leading-body-xs-medium ui-tracking-body-xs ui-text-link ui-underline'
              )}
            >
              {label}
            </a>
          </div>
        </div>
      );
    }

    if (kind === 'file') {
      const name = fileBlock.fileName ?? '';
      if (!name) return null;
      const downloadControl = fileBlock.onDownloadClick ? (
        <button
          type="button"
          className={cn(
            'ui-inline-flex ui-h-full ui-shrink-0 ui-items-center ui-justify-center ui-border-0 ui-bg-transparent ui-p-0',
            'focus-visible:ui-outline-brand focus-visible:ui-outline focus-visible:ui-outline-2 focus-visible:ui-outline-offset-2'
          )}
          aria-label="Descargar archivo"
          onClick={(e) => {
            e.stopPropagation();
            fileBlock.onDownloadClick?.();
          }}
        >
          <Icon name={faDownload} color="brand" size="sm" />
        </button>
      ) : (
        <div
          className="ui-flex ui-h-full ui-w-16 ui-shrink-0 ui-items-center ui-justify-center ui-self-stretch"
          aria-hidden
        >
          <Icon name={faDownload} color="brand" size="sm" />
        </div>
      );

      return (
        <div className={bodyRoot}>
          <div className={cn(PANEL_BASE, 'ui-w-full ui-items-center ui-gap-12')}>
            <div className="ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-leading-none">
              <Icon
                name={faFilePdf}
                color="brand"
                size="lg"
                className="ui-block ui-shrink-0 ui-object-contain"
              />
            </div>
            <div className="ui-flex ui-min-w-0 ui-flex-1 ui-flex-col ui-gap-0 ui-overflow-hidden ui-text-primary">
              <p
                className={cn(
                  'ui-w-full ui-min-w-0 ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap ui-text-body-xs-medium ui-font-body-xs-medium ui-leading-body-xs-medium ui-tracking-body-xs'
                )}
                title={name}
              >
                {name}
              </p>
              {fileBlock.sizeLabel ? (
                <p
                  className={cn(
                    'ui-w-full ui-min-w-0 ui-overflow-hidden ui-text-ellipsis ui-whitespace-nowrap ui-text-caption-xs ui-font-caption-xs ui-leading-caption-xs ui-tracking-caption-xs ui-text-secondary'
                  )}
                  title={fileBlock.sizeLabel}
                >
                  {fileBlock.sizeLabel}
                </p>
              ) : null}
            </div>
            <div className="ui-flex ui-flex-row ui-items-stretch">{downloadControl}</div>
          </div>
        </div>
      );
    }

    if (kind === 'cta') {
      const primaryLabel =
        ctaBlock.primaryButton?.label ??
        ctaBlock.primaryLabel ??
        events?.primaryButtonText ??
        'Text Button';
      const secondaryLabel =
        ctaBlock.secondaryButton?.label ??
        ctaBlock.secondaryLabel ??
        events?.secondaryButtonText ??
        'Text Button';
      const handlePrimaryClick = ctaBlock.primaryButton?.onClick ?? events?.onPrimaryClick;
      const handleSecondaryClick = ctaBlock.secondaryButton?.onClick ?? events?.onSecondaryClick;
      return (
        <div className={cn(bodyRoot, 'ui-mt-2 ui-flex ui-flex-wrap ui-items-center ui-gap-10')}>
          <Button
            theme="solid"
            color="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleSecondaryClick?.();
            }}
          >
            {secondaryLabel}
          </Button>
          <Button
            theme="solid"
            color="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handlePrimaryClick?.();
            }}
          >
            {primaryLabel}
          </Button>
        </div>
      );
    }

    return null;
  }
);

NotificationContentBody.displayName = 'NotificationContentBody';
